import { api } from './client';

// A curator-hosted voice room with capped, self-booked seats (cross-group).
export type SpeakingRoom = {
  id: string;
  curator_id: string;
  title: string;
  topic?: string;
  level?: string;
  scheduled_at: string;
  duration_min: number;
  capacity: number;
  status: 'scheduled' | 'active' | 'finished' | 'cancelled';
  host_name?: string;
  seats_taken: number;
  booked: boolean;

  // Кому комната адресована. ⚠️ Без этого комната показывалась ВСЕЙ школе:
  // колонки в базе были, а ни записывать, ни отбирать по ним не стали.
  audience_type?: 'all' | 'marathon' | 'group' | 'tariff';
  marathon_id?: string | null;
  group_id?: string | null;
  tariff?: string;
};

export type SpeakingRoomInput = {
  title: string;
  topic?: string;
  level?: string;
  scheduled_at: string; // ISO
  duration_min?: number;
  capacity?: number;
  audience_type?: 'all' | 'marathon' | 'group' | 'tariff';
  marathon_id?: string | null;
  group_id?: string | null;
  tariff?: string;
};

export type JoinInfo = {
  url: string;
  room: string;
  token: string;
  is_host: boolean;
  title: string;
  topic?: string;
  /** Когда комната заканчивается — для обратного отсчёта на экране. */
  ends_at?: string;
};

// Кто сейчас в комнате — по данным самого голосового сервера, а не по записям:
// записался ещё не значит зашёл.
export type RoomParticipant = {
  identity: string;
  name?: string;
  metadata?: string;
  state?: string;
};

export const speakingRoomsApi = {
  // ----- curator -----
  async listMine() {
    const { data } = await api.get('/curator/speaking-rooms');
    return data as { items: SpeakingRoom[]; total: number };
  },
  async create(input: SpeakingRoomInput) {
    const { data } = await api.post('/curator/speaking-rooms', input);
    return data as SpeakingRoom;
  },
  async update(id: string, input: SpeakingRoomInput) {
    await api.put(`/curator/speaking-rooms/${id}`, input);
  },
  async cancel(id: string) {
    await api.delete(`/curator/speaking-rooms/${id}`);
  },
  async joinAsHost(id: string) {
    const { data } = await api.post(`/curator/speaking-rooms/${id}/join`);
    return data as JoinInfo;
  },

  // ----- ведение комнаты -----
  // ⚠️ Доступно ЛЮБОМУ куратору и администратору, а не только тому, кто завёл
  // комнату (просьба заказчика): заглушить шумящего должен успеть тот, кто рядом.
  async participants(id: string) {
    const { data } = await api.get(`/curator/speaking-rooms/${id}/participants`);
    return (data?.items ?? []) as RoomParticipant[];
  },
  async setMuted(id: string, identity: string, muted: boolean) {
    await api.post(`/curator/speaking-rooms/${id}/mute`, { identity, muted });
  },
  async kick(id: string, identity: string) {
    await api.post(`/curator/speaking-rooms/${id}/kick`, { identity });
  },

  // ----- student -----
  async listOpen() {
    const { data } = await api.get('/student/speaking-rooms');
    return data as { items: SpeakingRoom[]; total: number };
  },
  async book(id: string) {
    await api.post(`/student/speaking-rooms/${id}/book`);
  },
  async cancelBooking(id: string) {
    await api.delete(`/student/speaking-rooms/${id}/book`);
  },
  async joinAsParticipant(id: string) {
    const { data } = await api.post(`/student/speaking-rooms/${id}/join`);
    return data as JoinInfo;
  },
};
